import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';

const COPY = {
  headline: 'Join the closed beta',
  subheadline:
    '',
  explanation: [
    'Beta users get full access and a direct feedback channel. After the beta, gymAhead continues as a paid product (€4.99/month). No credit card required during beta.',
  ],
  form: {
    emailLabel: 'Email',
    frequencyLabel: 'Training frequency',
    frequencyOptions: ['0–1 per week', '2–3 per week', '4+ per week'],
    rhythmLabel: 'What usually breaks your rhythm?',
    rhythmOptions: ['Work stress', 'Travel', 'Low energy', 'Illness', 'Other'],
    goalLabel: 'What do you want beh ead to help with? (optional)',
    consentLabel: 'I agree to be contacted about the beta.',
    submitLabel: 'Join the waitlist',
  },
  success: {
    title: "Thanks for signing up! We will reach out once a spot opens.",
    body: 'No dates. No promises.',
  },
};

export function BetaPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    email: '',
    trainingFrequency: '',
    rhythmBreakers: [] as string[],
    goalText: '',
    consent: false,
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    const email = form.email.trim();
    const emailLower = email.toLowerCase();
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailLower);

    if (!emailValid) {
      setError('Please enter a valid email.');
      return;
    }
    if (!form.trainingFrequency) {
      setError('Please select your training frequency.');
      return;
    }
    if (form.rhythmBreakers.length === 0) {
      setError('Please select what usually breaks your rhythm.');
      return;
    }
    if (!form.consent) {
      setError('Please confirm consent to be contacted.');
      return;
    }

    setSubmitting(true);

    const payload = {
      email,
      trainingFrequency: form.trainingFrequency,
      rhythmBreakers: form.rhythmBreakers,
      goalText: form.goalText.trim() || null,
      consent: form.consent,
      source: 'beta_page',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      locale: typeof navigator !== 'undefined' ? navigator.language : null,
    };

    try {
      await api.post('/beta-applications/submit', payload);
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit beta application', error);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <h1 className="text-3xl font-semibold text-neutral-900 mb-4">
            {COPY.success.title}
          </h1>
          <p className="text-base text-neutral-600 mb-8">{COPY.success.body}</p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="rounded-full bg-neutral-900 px-6 py-3 text-sm font-medium text-white transition hover:bg-neutral-800"
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-neutral-100 px-6 py-4">
        <div className="max-w-2xl mx-auto">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-sm text-neutral-500 hover:text-neutral-900 transition-colors"
          >
            ← Back
          </button>
        </div>
      </header>

      <main className="px-6 py-16">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-semibold text-neutral-900 mb-4">
            {COPY.headline}
          </h1>
          <p className="text-xl text-neutral-600 mb-8">{COPY.subheadline}</p>

          <div className="mb-12 space-y-3 text-base text-neutral-600">
            {COPY.explanation.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="beta-email">{COPY.form.emailLabel}</Label>
              <Input
                id="beta-email"
                type="email"
                value={form.email}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, email: event.target.value }))
                }
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="beta-frequency">{COPY.form.frequencyLabel}</Label>
              <select
                id="beta-frequency"
                value={form.trainingFrequency}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    trainingFrequency: event.target.value,
                  }))
                }
                className="flex h-10 w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-base text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2"
                required
              >
                <option value="" disabled>
                  Select an option
                </option>
                {COPY.form.frequencyOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-3">
              <Label>{COPY.form.rhythmLabel}</Label>
              <div className="space-y-2">
                {COPY.form.rhythmOptions.map((option) => (
                  <label
                    key={option}
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={form.rhythmBreakers.includes(option)}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setForm((prev) => ({
                            ...prev,
                            rhythmBreakers: [...prev.rhythmBreakers, option],
                          }));
                        } else {
                          setForm((prev) => ({
                            ...prev,
                            rhythmBreakers: prev.rhythmBreakers.filter(
                              (item) => item !== option
                            ),
                          }));
                        }
                      }}
                      className="h-4 w-4 rounded border-neutral-300 text-neutral-900 focus:ring-neutral-900"
                    />
                    <span className="text-base text-neutral-700">{option}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="beta-goal">{COPY.form.goalLabel}</Label>
              <textarea
                id="beta-goal"
                value={form.goalText}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    goalText: event.target.value,
                  }))
                }
                className="min-h-[96px] w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-base text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:ring-offset-2"
              />
            </div>

            <div className="flex items-start gap-3">
              <input
                id="beta-consent"
                type="checkbox"
                checked={form.consent}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    consent: event.target.checked,
                  }))
                }
                className="mt-1 h-4 w-4 rounded border-neutral-300 text-neutral-900 focus:ring-neutral-900"
                required
              />
              <Label htmlFor="beta-consent" className="text-sm text-neutral-600">
                {COPY.form.consentLabel}
              </Label>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <button
              type="submit"
              className="w-full rounded-full bg-neutral-900 px-6 py-3 text-base font-medium text-white transition hover:bg-neutral-800 disabled:opacity-60"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : COPY.form.submitLabel}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
